import { uploadReceiptToS3 } from 'shared/utils/receiptUpload'
import {
  autoApproveEligibleReimbursements,
  processReceiptForEmployee,
  resolveReimbursement,
} from './adminReceiptServices'
import { ProcessedReceipt } from './components/shared/ReceiptReviewModal'
import moment from 'moment'
import { CurrencyType } from 'features/Programs/Shared/types'
import { getCurrencySymbol } from 'shared/CurrencyFunctions'
import {
  ReimbursementBudgetUsed,
  ReimbursementEligibleBudget,
  ReimbursementReceiptDetails,
  ReimbursementStatus,
  ReimbursementSubmission,
  ResolveReimbursementPayload,
} from 'redux/reimbursements/reimbursementsTypes'

export const eligibleBudgetKey = (b: {
  budgetType: string
  programId: string | null
  cycleStart: string | null
  cycleEnd: string | null
  name: string
}): string =>
  `${b.budgetType}-${b.programId ?? 'no-program'}-${b.cycleStart ?? 'no-start'}-${
    b.cycleEnd ?? 'no-end'
  }-${b.name}`

export const buildNewManualProcessedReceipt = (
  index: number
): ProcessedReceipt => ({
  receiptId: `new-${Date.now()}-${index}`,
  merchant: 'Manual Upload',
  amount: 0,
  receiptDate: new Date().toISOString(),
  expenseType: null,
  merchantCategory: null,
  merchantCurrency: 'USD',
  merchantAmount: 0,
  manualReview: true,
  error: null,
})

export const isProcessedReceiptValid = (receipt: ProcessedReceipt): boolean => {
  const amountDollars = receipt.amount / 100
  return (
    amountDollars > 0 && !!receipt.receiptDate && !!receipt.merchantCurrency
  )
}

export const hasValidProcessedReceipts = (list: ProcessedReceipt[]): boolean =>
  list.length > 0 && list.every(isProcessedReceiptValid)

/** Parse an ISO date string into separate date and time strings for form inputs. */
export const parseDateTime = (isoString: string) => {
  const date = new Date(isoString)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  }
}

/** Parse a dollar input string to cents. Returns 0 for empty or invalid input. */
export const parseDollarInputToCents = (raw: string): number => {
  if (raw === '') return 0
  const parsed = Number(raw)
  return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100)
}

export const EMPLOYEE_NOTE_MAX_LENGTH = 255

export const getEmployeeNoteCharacterCount = (employeeNote: string): string =>
  `${employeeNote.length}/${EMPLOYEE_NOTE_MAX_LENGTH}`

/**
 * Format merchant amount in cents as a display string using only currency symbol,
 * without applying any conversion.
 */
export const formatMerchantCurrencyAmount = (
  currencyCode: string,
  amountInCents: number
): string => {
  const symbol = getCurrencySymbol((currencyCode || 'USD') as CurrencyType)
  const amount = ((amountInCents || 0) / 100).toFixed(2)
  return `${symbol}${amount}`
}

/**
 * Prefill budget allocations by priority: fill each eligible budget (in order) with
 * up to its available amount until receipt amount is fully allocated or all are at max.
 * Returns Record<eligibleBudgetKey, dollar string> for use in allocation inputs.
 */
export const getDefaultBudgetAllocations = (
  receiptAmountCents: number,
  eligibleBudgets: ReimbursementEligibleBudget[]
): Record<string, string> => {
  const result: Record<string, string> = {}
  if (!eligibleBudgets?.length) return result
  let remaining = receiptAmountCents
  const seen = new Set<string>()
  for (const b of eligibleBudgets) {
    const key = eligibleBudgetKey(b)
    if (seen.has(key)) continue
    seen.add(key)
    const allocateCents = remaining <= 0 ? 0 : Math.min(b.available, remaining)
    result[key] = (allocateCents / 100).toFixed(2)
    if (allocateCents > 0) remaining -= allocateCents
  }
  return result
}

/** Format budget cycle range for display (e.g. "Jan 1, 2025 - Jan 31, 2025"). */
export const formatBudgetCycleRange = (
  cycleStart: string | null,
  cycleEnd: string | null
): string => {
  if (!cycleStart || !cycleEnd) return '-'
  return `${moment.utc(cycleStart).format('MMM D, YYYY')} - ${moment
    .utc(cycleEnd)
    .format('MMM D, YYYY')}`
}

/** Pre-tax budgets: blue. Card program budgets: purple. */
export const getBudgetChipVariant = (budgetType: string): 'blue' | 'purple' => {
  const t = budgetType.toUpperCase()
  if (t.includes('CARD')) return 'purple'
  if (t.includes('PRETAX') || t.includes('PRE_TAX')) return 'blue'
  return 'blue'
}

export const getBudgetsSectionLabel = (status: ReimbursementStatus): string =>
  status === 'APPROVED' || status === 'PARTIAL'
    ? 'Budget allocation'
    : 'Eligible Budgets'

export type BudgetRow = {
  key: string
  label: string
  budgetType: string
  cycleStart: string | null
  cycleEnd: string | null
  available: number | null
  amountCents: number
  locked: boolean
}

export function buildBudgetRows(
  receipt: ReimbursementReceiptDetails,
  budgetAllocations: Record<string, string>
): BudgetRow[] {
  const isResolved =
    receipt.status === 'APPROVED' || receipt.status === 'PARTIAL'
  const used = receipt.budgetsUsed ?? []
  const eligible = receipt.eligibleBudgets ?? []

  if (isResolved && used.length) {
    return used.map((bu: ReimbursementBudgetUsed, idx) => ({
      key: `used-${idx}-${bu.budgetType}-${bu.programId ?? 'n'}`,
      label: bu.superProgramName || bu.name,
      budgetType: bu.budgetType,
      cycleStart: bu.cycleStart,
      cycleEnd: bu.cycleEnd,
      available: null,
      amountCents: bu.amount,
      locked: true,
    }))
  }

  if (isResolved && eligible.length) {
    const seen = new Set<string>()
    return eligible
      .filter((b) => {
        const key = eligibleBudgetKey(b)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map((b) => ({
        key: eligibleBudgetKey(b),
        label: b.superProgramName || b.name,
        budgetType: b.budgetType,
        cycleStart: b.cycleStart,
        cycleEnd: b.cycleEnd,
        available: b.available,
        amountCents: 0,
        locked: true,
      }))
  }

  return eligible.map((b) => {
    const key = eligibleBudgetKey(b)
    const raw = budgetAllocations[key] ?? ''
    return {
      key,
      label: b.superProgramName || b.name,
      budgetType: b.budgetType,
      cycleStart: b.cycleStart,
      cycleEnd: b.cycleEnd,
      available: b.available,
      amountCents: parseDollarInputToCents(raw),
      locked: false,
    }
  })
}

/** Whether an allocation input is invalid (non-empty but parse error, negative, or over max). */
export const isAllocationInputInvalid = (
  raw: string,
  maxAvailableCents: number
): boolean => {
  if (raw === '') return false
  const cents = parseDollarInputToCents(raw)
  return cents < 0 || cents > maxAvailableCents
}

export const getRemainingReimbursementBalance = (
  receiptAmountCents: number,
  eligibleBudgets: ReimbursementEligibleBudget[],
  budgetAllocations: Record<string, string>
): number | null => {
  if (!eligibleBudgets?.length) return null
  const seenKeys = new Set<string>()
  const totalAllocatedCents = eligibleBudgets.reduce((sum, b) => {
    const key = eligibleBudgetKey(b)
    if (seenKeys.has(key)) return sum
    seenKeys.add(key)
    const raw = budgetAllocations[key] ?? ''
    return sum + parseDollarInputToCents(raw)
  }, 0)
  return receiptAmountCents - totalAllocatedCents
}

/** True when any budget allocation amount (in cents) exceeds that budget's available amount. */
export const hasAnyAllocationOverAvailable = (
  receipt: { eligibleBudgets?: ReimbursementEligibleBudget[] } | null,
  budgetAllocations: Record<string, string>
): boolean => {
  if (!receipt?.eligibleBudgets?.length) return false
  const seenKeys = new Set<string>()
  for (const b of receipt.eligibleBudgets) {
    const key = eligibleBudgetKey(b)
    if (seenKeys.has(key)) continue
    seenKeys.add(key)
    const raw = budgetAllocations[key] ?? ''
    const cents = parseDollarInputToCents(raw)
    if (cents > 0 && cents > b.available) return true
  }
  return false
}

/** True when receipt details view should show the loading state (still loading or loaded wrong receipt). */
export const shouldShowReceiptDetailsLoading = (
  loading: boolean,
  receiptDetails: ReimbursementReceiptDetails | null,
  id: string | undefined
): boolean => {
  const hasDetails = receiptDetails != null
  const isStillLoading = loading || !hasDetails
  const loadedWrongReceipt =
    hasDetails && id != null && receiptDetails.id !== id
  return isStillLoading || loadedWrongReceipt
}

/** True when the receipt has budgets to show (eligible budgets or budgets used). */
export const shouldShowReimbursementBudgets = (
  receipt: {
    eligibleBudgets?: ReimbursementEligibleBudget[]
    budgetsUsed?: ReimbursementBudgetUsed[]
  } | null
): boolean => {
  if (!receipt) return false
  const hasEligible = (receipt.eligibleBudgets?.length ?? 0) > 0
  const hasUsed = (receipt.budgetsUsed?.length ?? 0) > 0
  return hasEligible || hasUsed
}

/** True when we should prefill budget allocation fields (under-review receipt, has eligible budgets, no existing allocations). */
export const shouldPrefillBudgetAllocations = (
  receipt: {
    id: string
    status: ReimbursementStatus
    amount: number
    eligibleBudgets?: ReimbursementEligibleBudget[]
  } | null,
  id: string | undefined,
  budgetAllocations: Record<string, string>
): boolean => {
  if (!receipt || !id) return false
  if (receipt.id !== id) return false
  if (receipt.status !== 'CREATED') return false
  if ((receipt.eligibleBudgets?.length ?? 0) === 0) return false
  if (Object.keys(budgetAllocations).length > 0) return false
  return true
}

/** True when the Approve button should be disabled. */
export const isApproveDisabled = (
  receipt: {
    status: ReimbursementStatus
    eligibleBudgets?: ReimbursementEligibleBudget[]
  } | null,
  resolving: boolean,
  remainingReimbursementBalance: number | null,
  budgetAllocations: Record<string, string>
): boolean => {
  if (!receipt) return true
  if (receipt.status !== 'CREATED' || resolving) return true
  if (hasAnyAllocationOverAvailable(receipt, budgetAllocations ?? {}))
    return true
  // Return true if the remaining balance is not 0 + null check
  return (
    remainingReimbursementBalance == null || remainingReimbursementBalance !== 0
  )
}

/** True when the Partial button should be disabled. */
export const isPartialDisabled = (
  receipt: {
    status: ReimbursementStatus
    amount: number
    eligibleBudgets?: ReimbursementEligibleBudget[]
  } | null,
  resolving: boolean,
  remainingReimbursementBalance: number | null,
  budgetAllocations: Record<string, string>
): boolean => {
  if (!receipt) return true
  if (receipt.status !== 'CREATED' || resolving) return true
  if (hasAnyAllocationOverAvailable(receipt, budgetAllocations ?? {}))
    return true
  if (remainingReimbursementBalance == null) return true
  if (remainingReimbursementBalance <= 0) return true
  if (remainingReimbursementBalance >= receipt.amount) return true
  return false
}

/**
 * Build the resolve payload from receipt, allocations, status, and note.
 * Used when approving, rejecting, or partially approving a reimbursement.
 */
export const buildResolvePayload = (
  receipt: ReimbursementReceiptDetails,
  budgetAllocations: Record<string, string>,
  status: ReimbursementStatus,
  note: string
): ResolveReimbursementPayload => {
  const seenKeys = new Set<string>()
  const budgets: ResolveReimbursementPayload['budgets'] = []
  let approvedAmount = 0

  if (status !== 'REJECTED' && receipt.eligibleBudgets?.length) {
    receipt.eligibleBudgets.forEach((b) => {
      const key = eligibleBudgetKey(b)
      if (seenKeys.has(key)) return
      seenKeys.add(key)
      const raw = budgetAllocations[key] ?? ''
      const cents = parseDollarInputToCents(raw)
      if (cents > 0) {
        budgets.push({
          budgetType: b.budgetType,
          ...(b.programId ? { budgetProgramId: b.programId } : {}),
          amount: cents,
        })
        approvedAmount += cents
      }
    })
  }

  if (status === 'REJECTED') {
    approvedAmount = 0
  } else if (status === 'APPROVED' && budgets.length === 0) {
    approvedAmount = receipt.amount
  }

  return {
    status,
    approvedAmount,
    budgets,
    note: note || undefined,
  }
}

/**
 * Build payload and call API to resolve a reimbursement. Returns { status: 'success' } or { status: 'error', error: string }.
 */
export const executeResolveReimbursement = async (
  receiptId: string,
  receipt: ReimbursementReceiptDetails,
  budgetAllocations: Record<string, string>,
  status: ReimbursementStatus,
  note: string
) => {
  const payload = buildResolvePayload(receipt, budgetAllocations, status, note)
  return resolveReimbursementForEmployee(receiptId, payload)
}

export type ReimbursementAdminStatusFilterOption =
  | 'all'
  | 'CREATED'
  | 'APPROVED'
  | 'PARTIAL'
  | 'REJECTED'

export const getReimbursementAdminCounts = (
  submissions: ReimbursementSubmission[] | null
): Record<ReimbursementAdminStatusFilterOption, number> => {
  const statusCounts: Record<ReimbursementAdminStatusFilterOption, number> = {
    all: submissions?.length ?? 0,
    CREATED: 0,
    APPROVED: 0,
    PARTIAL: 0,
    REJECTED: 0,
  }

  submissions?.forEach((s) => {
    if (s.status === 'CREATED') {
      statusCounts.CREATED += 1
    }
    if (s.status === 'APPROVED') statusCounts.APPROVED += 1
    if (s.status === 'PARTIAL') statusCounts.PARTIAL += 1
    if (s.status === 'REJECTED') statusCounts.REJECTED += 1
  })

  return statusCounts
}

export const uploadAndProcessReceiptForEmployee = async (
  file: File,
  employeeId: string
) => {
  try {
    const uploadResult = await uploadReceiptToS3(file)
    if (uploadResult.status === 'error') {
      return uploadResult
    }

    const processResponse = await processReceiptForEmployee(
      uploadResult.downloadUrl,
      file.type,
      employeeId
    )
    if (!processResponse?.data) {
      return { status: 'error', error: 'Receipt processing failed' }
    }

    return {
      status: 'success',
      downloadUrl: processResponse.data.downloadUrl ?? '',
      contentType: processResponse.data.contentType ?? '',
      receipts: processResponse.data.receipts ?? [],
    }
  } catch (error) {
    return { status: 'error', error: (error as Error).message }
  }
}

export const resolveReimbursementForEmployee = async (
  receiptId: string,
  payload: ResolveReimbursementPayload
) => {
  try {
    const response = await resolveReimbursement(receiptId, payload)
    if (!response?.data) {
      return { status: 'error', error: 'Failed to resolve reimbursement' }
    }
    return { status: 'success' }
  } catch (error) {
    return { status: 'error', error: (error as Error).message }
  }
}

export const autoApproveEligibleReimbursementsForEmployer = async (
  employerId: string
) => {
  try {
    const response = await autoApproveEligibleReimbursements(employerId)
    if (!response) {
      return { status: 'error', error: 'Failed to auto approve reimbursements' }
    }
    return { status: 'success' }
  } catch (error) {
    return { status: 'error', error: (error as Error).message }
  }
}
